// React Query
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
// Http Methods
import { getRequest as get } from '../utils/HttpMethods';

//Utils
import queryString from '../utils/queryStringBuilder';

// Interfaces
import { Credit, isCredit } from 'interfaces';
import { createEntity, useListQuery, useMutationQuery } from '../utils/queries';

export const creditQueryKeys = {
  all: ['credits'],
  list: () => [...creditQueryKeys.all, 'list'],
  getCreditsBySite: (siteID: string) => [...creditQueryKeys.all, siteID],
  get: (id: string) => [...creditQueryKeys.all, id],
  creditSumByUser: (userID: string) => [
    ...creditQueryKeys.all,
    'creditSum',
    userID,
  ],
};

/**
 * @description Fetches credits by site ID.
 * @param {string} siteID - The ID of the site.
 * @returns {Promise<Credit[]>} The credits for the specified site.
 */
const getCreditsBySite = async (siteID: string): Promise<Credit[]> =>
  await get<Credit[]>(
    'QBilling',
    `/resource/credit/site${queryString({ siteID })}`
  );

const createCredit = async (credit: Credit): Promise<Credit> =>
  createEntity<Credit>({
    entity: credit,
    functionName: 'createCredit',
    isEntity: isCredit,
    path: '/resource/credit',
  });

/**
 * @description Hook to fetch credits by site ID.
 * @param {Object} params - The parameters for the request.
 * @param {string} params.siteID - The ID of the site.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<Credit[], Error>} The credits for the specified site.
 */
export const useGetCreditsBySite = ({
  siteID,
  displayNotification = false,
}: {
  siteID: string;
  displayNotification?: boolean;
}): UseQueryResult<Credit[], Error> => {
  return useListQuery<Credit>({
    queryKey: creditQueryKeys.getCreditsBySite(siteID),
    fetchFunction: getCreditsBySite,
    functionName: 'getCreditsBySite',
    isEntity: isCredit,
    params: siteID,
    enabled: !!siteID,
    displayNotification,
  });
};

export const useCreateCredit = (): UseMutationResult<Credit, Error> =>
  useMutationQuery<Credit>({
    mutationFn: createCredit,
    errorMessage: 'Failed to create credit',
    successMessage: 'Credit created successfully',
    queryKey: creditQueryKeys.all,
  });
